import * as React from "react";
import { useId, useMemo } from "react";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import type { City, Location } from "modules/settings/types/location-types";
import {
  ONBOARDING_FALLBACK_COMUNA_ID,
  ONBOARDING_FALLBACK_COMUNA_LABEL,
} from "modules/onboarding/types/onboarding-state";

/**
 * Payload reported to the parent whenever the user picks a comuna or a
 * sector. Mirrors the typed contract declared in the change design.
 */
export interface LocationSelectorFieldsChangePayload {
  cityId: string;
  cityName: string;
  locationId: string;
  locationName: string;
  isFallback: boolean;
}

/** Controlled value shape. Both ids are strings because Radix Select is string-based. */
export interface LocationSelectorFieldsValue {
  cityId: string;
  locationId: string;
}

export interface LocationSelectorFieldsProps {
  cities: City[];
  locations: Location[];
  loading: boolean;
  value: LocationSelectorFieldsValue;
  onChange: (payload: LocationSelectorFieldsChangePayload) => void;
  /** When true, append the fallback option to the Comuna dropdown. */
  includeFallback?: boolean;
  /** Optional error message rendered below the controls. */
  error?: string;
}

/**
 * Shared comuna/sector selector fields.
 *
 * Controlled presentational component extracted from
 * `location-selector.tsx`. The original dialog keeps its submit + backend
 * update path around this shared UI; onboarding consumes the same fields
 * in external-submit mode (no submit button, no `updateLocationEndPoint`
 * call, fallback comuna option available).
 */
export const LocationSelectorFields: React.FC<LocationSelectorFieldsProps> = ({
  cities,
  locations,
  loading,
  value,
  onChange,
  includeFallback = false,
  error,
}) => {
  const cityFieldId = useId();
  const sectorFieldId = useId();

  const filteredLocations = useMemo(() => {
    if (!value.cityId || value.cityId === ONBOARDING_FALLBACK_COMUNA_ID.toString()) {
      return [] as Location[];
    }
    const numericCityId = Number(value.cityId);
    if (Number.isNaN(numericCityId)) {
      return [] as Location[];
    }
    return locations.filter((location) => location.city_id === numericCityId);
  }, [locations, value.cityId]);

  const displayableCities = useMemo(
    () => cities.filter((c) => c.id !== 4),
    [cities],
  );

  const displayableLocations = useMemo(
    () => filteredLocations.filter((l) => l.id !== 1),
    [filteredLocations],
  );

  const isFallbackSelected =
    value.cityId === ONBOARDING_FALLBACK_COMUNA_ID.toString();

  const sectorDisabled =
    loading || !value.cityId || isFallbackSelected || displayableLocations.length === 0;

  const handleCityChange = (nextCityId: string) => {
    if (nextCityId === ONBOARDING_FALLBACK_COMUNA_ID.toString()) {
      onChange({
        cityId: ONBOARDING_FALLBACK_COMUNA_ID.toString(),
        cityName: ONBOARDING_FALLBACK_COMUNA_LABEL,
        locationId: "",
        locationName: "",
        isFallback: true,
      });
      return;
    }
    const numericCityId = Number(nextCityId);
    const city = cities.find((c) => c.id === numericCityId);
    onChange({
      cityId: nextCityId,
      cityName: city?.name ?? "",
      locationId: "",
      locationName: "",
      isFallback: false,
    });
  };

  const handleLocationChange = (nextLocationId: string) => {
    const numericLocationId = Number(nextLocationId);
    const location = locations.find((l) => l.id === numericLocationId);
    const currentCity = cities.find((c) => c.id === Number(value.cityId));
    onChange({
      cityId: value.cityId,
      cityName: currentCity?.name ?? "",
      locationId: nextLocationId,
      locationName: location?.name ?? "",
      isFallback: isFallbackSelected,
    });
  };

  return (
    <div className="flex flex-col space-y-4">
      <div className="flex flex-col gap-2">
        <Label htmlFor={cityFieldId} className="text-xs">
          Comuna
        </Label>
        <Select
          value={value.cityId || undefined}
          onValueChange={handleCityChange}
          disabled={loading}
        >
          <SelectTrigger
            id={cityFieldId}
            aria-label="Comuna"
            className="text-xs notranslate"
          >
            <SelectValue placeholder="Selecciona tu comuna" />
          </SelectTrigger>
          <SelectContent className="overflow-y-auto max-h-[15rem]">
            {loading ? (
              <div className="flex justify-center p-2">
                <div
                  className="animate-spin h-5 w-5 border-2 border-primary rounded-full border-t-transparent"
                  aria-label="Cargando comunas"
                />
              </div>
            ) : (
              <SelectGroup>
                {displayableCities.map((city) => (
                  <SelectItem
                    key={city.id}
                    value={city.id.toString()}
                    className="text-xs"
                  >
                    <span>{city.name}</span>
                  </SelectItem>
                ))}
                {includeFallback && (
                  <SelectItem
                    key="__onboarding_fallback__"
                    value={ONBOARDING_FALLBACK_COMUNA_ID.toString()}
                    className="text-xs"
                  >
                    <span>{ONBOARDING_FALLBACK_COMUNA_LABEL}</span>
                  </SelectItem>
                )}
              </SelectGroup>
            )}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-col gap-2">
        <Label htmlFor={sectorFieldId} className="text-xs">
          Sector
        </Label>
        <Select
          value={value.locationId || undefined}
          onValueChange={handleLocationChange}
          disabled={sectorDisabled}
        >
          <SelectTrigger
            id={sectorFieldId}
            aria-label="Sector"
            className="text-xs notranslate"
          >
            <SelectValue placeholder="Selecciona tu sector" />
          </SelectTrigger>
          <SelectContent>
            {loading ? (
              <div className="flex justify-center p-2">
                <div
                  className="animate-spin h-5 w-5 border-2 border-primary rounded-full border-t-transparent"
                  aria-label="Cargando sectores"
                />
              </div>
            ) : (
              <SelectGroup>
                {displayableLocations.map((location) => (
                  <SelectItem
                    key={location.id}
                    value={location.id.toString()}
                    className="text-xs"
                  >
                    <span>{location.name}</span>
                  </SelectItem>
                ))}
              </SelectGroup>
            )}
          </SelectContent>
        </Select>
      </div>

      {error ? (
        <p className="text-xs text-destructive" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
};
